import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/** One tulip in the meadow. */
interface Tulip {
  x: number;
  y: number;
  h: number;
  size: number;
  color: RGB;
  phase: number;
  rate: number;
  lean: number;
}

/** One of the butterflies over the meadow (Dock Town's Spring). */
interface Butterfly {
  x: number;
  y: number;
  vx: number;
  phase: number;
  size: number;
  color: RGB;
  rank: number;
  shown: number;
  bob: number;
}

/**
 * Spring's hero board (2026-10-17): a meadow in bloom, light and gentle like the theme. A soft sky
 * over rolling hills that fade with distance, then the meadow, and in it a field of tulips in the
 * circle's rose and lavender with a little sun yellow, small and many far off, large and few in front,
 * every one swaying on its stem in the breeze; and butterflies, as in Dock Town's Spring, fluttering
 * over it. A gust (the pointer, a finger, the counter counting) brings more butterflies out, quicker,
 * and makes the breeze stronger. Laid out by the stage's height with fixed seeds.
 */
@Component( {
  selector: 'app-hero-spring',
  template: '<canvas #canvas class="hsp-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hsp-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroSpringComponent extends HeroMotifBase {

  private back: HTMLCanvasElement | null = null;
  private tulips: Tulip[] = [];
  private flies: Butterfly[] = [];
  private scaleK = 1;
  private meadowTop = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
    this.build();
  }

  protected build(): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    this.back = null;
    // the meadow and the hills behind it a tick higher (owner)
    this.meadowTop = H * 0.62 + 10;
    const [ , , rose, lavender, teal ] = this.five();
    const sun: RGB = [ 242, 193, 78 ];
    const blooms: RGB[] = [ rose, lavender, rose, lavender, this.mix( rose, [ 255, 255, 255 ], 0.35 ), sun ];
    const rand = this.seededLocal( 7 );
    // the tulips: rows from far (small, many) to near (large, few), sorted far to near for drawing
    this.tulips = [];
    // a dense field, thickest toward the back (owner)
    for ( let i = 0; i < 760; i++ ) {
      const depth = Math.pow( rand(), 2.1 );
      const y = this.meadowTop + H * 0.02 + ( H - this.meadowTop ) * depth * 1.02;
      const near = ( y - this.meadowTop ) / ( H - this.meadowTop );
      const size = ( 3 + 26 * Math.pow( near, 1.6 ) ) * k;
      this.tulips.push( {
        x: rand() * W, y, h: size * ( 3.2 + rand() * 1.2 ), size, color: blooms[ Math.floor( rand() * blooms.length ) ],
        phase: rand() * Math.PI * 2, rate: 2 + Math.floor( rand() * 3 ), lean: ( rand() - 0.5 ) * 0.2,
      } );
    }
    // and five times as many again toward the back, small with distance (owner)
    // (fewer than before: the board stuttered, owner)
    for ( let i = 0; i < 1300; i++ ) {
      const depth = Math.pow( rand(), 2.6 ) * 0.55;
      const y = this.meadowTop + H * 0.01 + ( H - this.meadowTop ) * depth;
      const near = ( y - this.meadowTop ) / ( H - this.meadowTop );
      const size = ( 2.2 + 26 * Math.pow( near, 1.6 ) ) * k;
      this.tulips.push( {
        x: rand() * W, y, h: size * ( 3.2 + rand() * 1.2 ), size, color: blooms[ Math.floor( rand() * blooms.length ) ],
        phase: rand() * Math.PI * 2, rate: 2 + Math.floor( rand() * 3 ), lean: ( rand() - 0.5 ) * 0.2,
      } );
    }
    // a strip of bare meadow left between the farthest tulips and the trees (owner)
    // and none whose flower would rise in front of the bushes (owner)
    this.tulips = this.tulips.filter( p => p.y > this.meadowTop + H * 0.022 && p.y - p.h - p.size > this.meadowTop + H * 0.012 );
    this.tulips.sort( ( a, b ) => a.y - b.y );
    // the butterflies: a pool, each out only while the gust is strong enough for its rank
    this.flies = [];
    const wings: RGB[] = [ lavender, sun, rose, teal ];
    for ( let i = 0; i < 30; i++ ) {
      this.flies.push( {
        // low over the flowers, not up in the sky (owner)
        x: rand() * W, y: this.meadowTop - H * 0.06 + rand() * ( H * 0.98 - this.meadowTop ), vx: ( rand() < 0.5 ? -1 : 1 ) * ( 18 + rand() * 26 ) * k,
        phase: rand() * Math.PI * 2, size: ( 9 + rand() * 7 ) * k, color: wings[ i % wings.length ],
        rank: i < 3 ? i * 0.03 : rand(), shown: 0, bob: rand() * Math.PI * 2,
      } );
    }
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    const scale = ctx.getTransform().a;
    if ( this.frozen ) {
      // the page's wallpaper: the butterflies alone, still, all down the page
      this.drawWallButterflies( ctx );
      return;
    }
    if ( !this.back ) { this.bake( scale ); }
    if ( !this.back ) { return; }
    ctx.drawImage( this.back, 0, 0, W, H );
    this.drawTulips( ctx );
    this.drawButterflies( ctx, dt );
    void H;
  }

  /**
   * The tulips, far to near, each swaying on its stem, the breeze stronger in a gust. Two passes: every
   * stem and leaf first, then every bloom, so no stem ever crosses a flower (owner).
   */
  private drawTulips( ctx: CanvasRenderingContext2D ): void {
    const t = this.time * W0;
    const e = this.energy;
    const [ , green ] = this.five();
    const H = this.height;
    const pose = this.tulips.map( p => {
      const near = ( p.y - this.meadowTop ) / ( H - this.meadowTop );
      const sway = ( Math.sin( t * p.rate + p.phase ) * 0.08 + Math.sin( t * 5 + p.x * 0.004 ) * 0.05 ) * ( 1 + 1.2 * e ) + p.lean;
      // the far ones sink into the haze of the meadow
      const haze = 1 - Math.min( 1, near * 1.6 );
      return { p, sway, haze, tx: p.x + Math.sin( sway ) * p.h, ty: p.y - Math.cos( sway ) * p.h };
    } );
    // the stems and their leaves
    for ( const { p, sway, haze, tx, ty } of pose ) {
      if ( p.size < 5 * this.scaleK ) { continue; }
      const stem = this.mix( this.mix( green, [ 30, 70, 40 ], 0.2 ), [ 200, 230, 200 ], haze * 0.55 );
      // a long broad leaf from the foot, curving out, with a paler midrib
      const leafSide = p.phase > Math.PI ? 1 : -1;
      const lx = p.x + leafSide * p.size * 0.5 + Math.sin( sway ) * p.h * 0.3, ly = p.y - p.h * 0.66;
      const lg = ctx.createLinearGradient( p.x, p.y, lx, ly );
      lg.addColorStop( 0, this.rgba( this.mix( stem, [ 20, 50, 30 ], 0.15 ), 1 ) );
      lg.addColorStop( 1, this.rgba( this.mix( stem, [ 230, 245, 220 ], 0.12 ), 1 ) );
      ctx.fillStyle = lg;
      ctx.beginPath();
      ctx.moveTo( p.x - leafSide * p.size * 0.1, p.y );
      ctx.bezierCurveTo( p.x + leafSide * p.size * 1.0, p.y - p.h * 0.18, p.x + leafSide * p.size * 0.9, p.y - p.h * 0.45, lx, ly );
      ctx.bezierCurveTo( p.x + leafSide * p.size * 0.3, p.y - p.h * 0.4, p.x + leafSide * p.size * 0.15, p.y - p.h * 0.2, p.x + leafSide * p.size * 0.1, p.y );
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = this.rgba( this.mix( stem, [ 240, 250, 235 ], 0.25 ), 0.5 );
      ctx.lineWidth = Math.max( 0.4, p.size * 0.03 );
      ctx.beginPath();
      ctx.moveTo( p.x, p.y );
      ctx.quadraticCurveTo( p.x + leafSide * p.size * 0.6, p.y - p.h * 0.35, lx, ly );
      ctx.stroke();
      // the stem, tapering a little, curving with the sway
      ctx.strokeStyle = this.rgba( stem, 1 );
      ctx.lineWidth = Math.max( 0.8, p.size * 0.11 );
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo( p.x, p.y );
      ctx.quadraticCurveTo( p.x + Math.sin( sway ) * p.h * 0.3, p.y - p.h * 0.55, tx, ty );
      ctx.stroke();
    }
    // the blooms, over every stem
    for ( const { p, sway, haze, tx, ty } of pose ) {
      const bloom = this.mix( p.color, [ 245, 250, 240 ], haze * 0.45 );
      if ( p.size < 5 * this.scaleK ) {
        // far off: just a dab of colour
        ctx.fillStyle = this.rgba( bloom, 0.85 );
        ctx.beginPath();
        ctx.ellipse( tx, ty, p.size * 0.5, p.size * 0.7, sway, 0, Math.PI * 2 );
        ctx.fill();
        continue;
      }
      this.tulipBloom( ctx, tx, ty, sway, p.size, bloom );
    }
  }

  /**
   * One tulip's bloom, an egg-shaped cup of six petals as the real flower is: the inner three
   * showing their pointed tips at the back, the outer three in front, rounded at the top and
   * overlapping, each shaded deeper at its base and lit along one side, a soft sheen down the middle.
   */
  private tulipBloom( ctx: CanvasRenderingContext2D, x: number, y: number, angle: number, s: number, bloom: RGB ): void {
    const dark = this.mix( bloom, [ 40, 10, 20 ], 0.22 );
    const light = this.mix( bloom, [ 255, 255, 255 ], 0.3 );
    ctx.save();
    ctx.translate( x, y );
    ctx.rotate( angle );
    // the inner petals' tips, behind
    ctx.fillStyle = this.rgba( this.mix( bloom, dark, 0.45 ), 1 );
    for ( const dx of [ -0.22, 0.22 ] ) {
      ctx.beginPath();
      ctx.moveTo( dx * s - 0.18 * s, -0.55 * s );
      ctx.quadraticCurveTo( dx * s, -1.2 * s, dx * s + 0.18 * s, -0.55 * s );
      ctx.closePath();
      ctx.fill();
    }
    // the outer petals: left, right, then the middle one in front
    const petal = ( cx: number, w: number, top: number, tilt: number ) => {
      ctx.save();
      ctx.translate( cx * s, 0 );
      ctx.rotate( tilt );
      const g = ctx.createLinearGradient( -w * s, 0, w * s, -top * s );
      g.addColorStop( 0, this.rgba( dark, 1 ) );
      g.addColorStop( 0.5, this.rgba( bloom, 1 ) );
      g.addColorStop( 1, this.rgba( light, 1 ) );
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo( 0, 0 );
      ctx.bezierCurveTo( -w * 1.25 * s, -0.25 * s, -w * 1.1 * s, -top * 0.92 * s, 0, -top * s );
      ctx.bezierCurveTo( w * 1.1 * s, -top * 0.92 * s, w * 1.25 * s, -0.25 * s, 0, 0 );
      ctx.fill();
      // the sheen down its middle
      ctx.strokeStyle = this.rgba( light, 0.35 );
      ctx.lineWidth = Math.max( 0.5, s * 0.06 );
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo( 0, -top * 0.2 * s );
      ctx.quadraticCurveTo( w * 0.25 * s, -top * 0.55 * s, 0, -top * 0.85 * s );
      ctx.stroke();
      ctx.restore();
    };
    petal( -0.2, 0.36, 0.98, -0.16 );
    petal( 0.2, 0.36, 0.98, 0.16 );
    petal( 0, 0.4, 1.02, 0 );
    // the base where the petals meet the stem, shaded
    const bg = ctx.createRadialGradient( 0, 0, 0, 0, 0, 0.35 * s );
    bg.addColorStop( 0, this.rgba( dark, 0.6 ) );
    bg.addColorStop( 1, this.rgba( dark, 0 ) );
    ctx.fillStyle = bg;
    ctx.fillRect( -0.35 * s, -0.35 * s, 0.7 * s, 0.4 * s );
    ctx.restore();
  }

  /**
   * The butterflies: each flutters (its wings closing and opening, seen as they narrow and widen) and
   * dances its way across, rising and dipping; a few at rest, many more and quicker in a gust, each
   * coming out somewhere and fading in, and fading away when sent off.
   */
  private drawButterflies( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    const e = this.energy;
    const share = 0.1 + 0.9 * e;
    for ( const b of this.flies ) {
      const want = b.rank <= share ? 1 : 0;
      if ( dt > 0 ) {
        if ( want && b.shown < 0.02 ) { b.x = Math.random() * W; b.y = this.meadowTop - H * 0.06 + Math.random() * ( H * 0.98 - this.meadowTop ); }
        b.shown += ( want - b.shown ) * Math.min( 1, dt * ( want > b.shown ? 1 : 0.5 ) );
        b.x += b.vx * ( 1 + 1.2 * e ) * dt;
        b.phase += dt * ( 14 + 6 * e );
        b.bob += dt * 1.7;
        const m = b.size * 3;
        if ( b.x > W + m ) { b.x = -m; } else if ( b.x < -m ) { b.x = W + m; }
      } else {
        b.shown = want;
      }
      if ( b.shown < 0.02 ) { continue; }
      const y = b.y + Math.sin( b.bob ) * b.size * 2.2 + Math.sin( b.bob * 2.3 ) * b.size;
      ctx.save();
      ctx.globalAlpha = b.shown;
      ctx.translate( b.x, y );
      ctx.rotate( Math.sin( b.bob * 1.3 ) * 0.25 );
      this.butterfly( ctx, b.size, b.color, Math.abs( Math.cos( b.phase ) ) );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * A butterfly as Dock Town draws it, seen from above: two pairs of wings, the fore wings larger
   * with a white spot, the hind wings a shade darker, the dark body between; `open` is how wide its
   * wings stand (1 spread, near 0 closed edge-on).
   */
  private butterfly( ctx: CanvasRenderingContext2D, s: number, wing: RGB, open: number ): void {
    const w = 0.15 + 0.85 * open;
    for ( const side of [ -1, 1 ] ) {
      ctx.fillStyle = this.rgba( wing, 1 );
      ctx.beginPath();
      ctx.ellipse( side * 0.42 * s * w, -0.28 * s, 0.4 * s * w, 0.34 * s, side * 0.25, 0, Math.PI * 2 );
      ctx.fill();
      ctx.fillStyle = this.rgba( this.mix( wing, [ 0, 0, 0 ], 0.15 ), 1 );
      ctx.beginPath();
      ctx.ellipse( side * 0.3 * s * w, 0.22 * s, 0.26 * s * w, 0.22 * s, -side * 0.2, 0, Math.PI * 2 );
      ctx.fill();
      ctx.fillStyle = this.rgba( [ 255, 255, 255 ], 0.8 );
      ctx.beginPath();
      ctx.arc( side * 0.45 * s * w, -0.3 * s, 0.1 * s * Math.max( 0.3, w ), 0, Math.PI * 2 );
      ctx.fill();
    }
    ctx.strokeStyle = 'rgb(42,42,42)';
    ctx.lineWidth = Math.max( 0.8, 0.12 * s );
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo( 0, -0.45 * s );
    ctx.lineTo( 0, 0.45 * s );
    ctx.stroke();
  }

  /** The wallpaper: butterflies scattered still down the page, each caught mid-flutter. */
  private drawWallButterflies( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const rand = this.seededLocal( 5 );
    const [ , , rose, lavender, teal ] = this.five();
    const wings: RGB[] = [ lavender, [ 242, 193, 78 ], rose, teal ];
    const n = Math.max( 6, Math.round( W * H / 70000 ) );
    for ( let i = 0; i < n; i++ ) {
      ctx.save();
      ctx.globalAlpha = 0.55 + rand() * 0.3;
      ctx.translate( rand() * W, rand() * H );
      ctx.rotate( ( rand() - 0.5 ) * 0.8 );
      this.butterfly( ctx, ( 7 + rand() * 6 ) * k, wings[ i % wings.length ], 0.3 + rand() * 0.7 );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * The still picture, painted once: the sky, the rolling hills fading with distance and green
   * trees scattered over them, the meadow with its fine grass, and a clearing of
   * light behind the hero's words.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const UW = Math.min( W, H * 2.1 );
    const [ pale, green, , lavender, teal ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    const c = document.createElement( 'canvas' );
    c.width = Math.ceil( W * scale );
    c.height = Math.ceil( H * scale );
    const g = c.getContext( '2d' )!;
    g.scale( scale, scale );
    const horizon = this.meadowTop;
    // the sky as it is on a sunny day (owner): a clear blue, deepest high up and away from the sun,
    // paling toward the hills; near the sun the blue turns to a bright white glare, never yellow
    const sky = g.createLinearGradient( 0, 0, 0, horizon );
    sky.addColorStop( 0, this.rgba( this.mix( this.mix( teal, [ 70, 150, 225 ], 0.55 ), white, 0.3 ), 1 ) );
    sky.addColorStop( 0.55, this.rgba( this.mix( this.mix( teal, [ 110, 180, 235 ], 0.5 ), white, 0.58 ), 1 ) );
    sky.addColorStop( 1, this.rgba( this.mix( pale, white, 0.6 ), 1 ) );
    g.fillStyle = sky;
    g.fillRect( 0, 0, W, H );
    const R = Math.max( W, H );
    const sunX = W / 2 + UW * 0.38, sunY = -H * 0.04;
    const glare = g.createRadialGradient( sunX, sunY, 0, sunX, sunY, R * 0.75 );
    glare.addColorStop( 0, this.rgba( [ 255, 255, 250 ], 0.95 ) );
    glare.addColorStop( 0.08, this.rgba( [ 255, 254, 245 ], 0.75 ) );
    glare.addColorStop( 0.3, this.rgba( [ 250, 252, 255 ], 0.28 ) );
    glare.addColorStop( 1, this.rgba( [ 250, 252, 255 ], 0 ) );
    g.fillStyle = glare;
    g.fillRect( 0, 0, W, horizon + 2 );
    // a few soft clouds
    const cr = this.seededLocal( 3 );
    for ( let i = 0; i < 7; i++ ) {
      g.save();
      g.translate( W / 2 + ( cr() - 0.5 ) * 1.2 * Math.max( W, UW ), H * ( 0.06 + cr() * 0.26 ) );
      g.scale( ( 70 + cr() * 140 ) * k, ( 14 + cr() * 14 ) * k );
      const wg = g.createRadialGradient( 0, -0.2, 0, 0, 0, 1 );
      wg.addColorStop( 0, this.rgba( white, 0.6 ) );
      wg.addColorStop( 1, this.rgba( white, 0 ) );
      g.fillStyle = wg;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    // the hills: three layers rolling away, paler and bluer the farther, drawn at full size with
    // clean smooth ridges (owner: the same drawing, sharper, no extra layers)
    const layers: [ number, number, number, RGB ][] = [
      [ horizon - H * 0.1, H * 0.08, 31, this.mix( this.mix( green, teal, 0.4 ), white, 0.62 ) ],
      [ horizon - H * 0.05, H * 0.07, 32, this.mix( this.mix( green, pale, 0.4 ), white, 0.42 ) ],
      // the front hill lower, its trees coming down with it (owner)
      [ horizon + H * 0.005, H * 0.032, 33, this.mix( green, pale, 0.45 ) ],
    ];
    const deferred: ( () => void )[] = [];
    for ( const [ base, amp, seed, tone ] of layers ) {
      const rr = this.seededLocal( seed );
      const waves = [ 0, 1, 2 ].map( i => ( { f: 2.4 * Math.pow( 2.2, i ), p: rr() * 6.28, a: Math.pow( 0.5, i ) } ) );
      const hg = g.createLinearGradient( 0, base - amp, 0, base + H * 0.04 );
      hg.addColorStop( 0, this.rgba( this.mix( tone, white, 0.12 ), 1 ) );
      hg.addColorStop( 1, this.rgba( tone, 1 ) );
      g.fillStyle = hg;
      g.beginPath();
      g.moveTo( -2, H );
      for ( let x = -2; x <= W + 2; x += 2 ) {
        const t = ( x - W / 2 ) / UW;
        let h = 0;
        for ( const w of waves ) { h += w.a * ( 0.5 + 0.5 * Math.sin( t * w.f + w.p ) ); }
        g.lineTo( x, base - amp * h / 1.75 );
      }
      g.lineTo( W + 2, H );
      g.closePath();
      g.fill();
      // trees over this hill's slopes, gathered in tidy copses rather than one by one, never in a row; on the far hills flat silhouettes, smaller
      // and paler, on the nearest hill drawn properly: light brown trunks, crowns lit from the sun at
      // the right and shaded on the left (owner)
      const far = layers.findIndex( l2 => l2[ 2 ] === seed );
      // the middle hill's copses fainter, half lost in the haze (owner)
      const fade = [ 0.55, 0.62, 0.1 ][ far ];
      const scaleT = [ 0.45, 0.7, 1 ][ far ];
      const detailed = far === 2;
      const ridgeAt = ( x: number ) => {
        const t = ( x - W / 2 ) / UW;
        let h = 0;
        for ( const w of waves ) { h += w.a * ( 0.5 + 0.5 * Math.sin( t * w.f + w.p ) ); }
        return base - amp * h / 1.75;
      };
      // the one line along the hill on which every tree of this hill stands
      const footLine = ( x: number ) => { const r = ridgeAt( x ); return r + ( base + H * 0.03 - r ) * 0.45; };
      const tr = this.seededLocal( seed + 40 );
      const trunkTone = this.mix( [ 168, 128, 92 ], white, fade );
      const crown = ( cx: number, cy: number, R2: number, tone: RGB, puffs: number, soft = false ) => {
        if ( !detailed ) {
          g.fillStyle = this.rgba( tone, 1 );
          g.beginPath();
          for ( let j = 0; j < puffs; j++ ) {
            const a2 = tr() * Math.PI * 2, d = Math.sqrt( tr() ) * R2 * 0.55;
            const px = cx + Math.cos( a2 ) * d, py = cy + Math.sin( a2 ) * d * 0.8, pr = R2 * ( 0.45 + tr() * 0.25 );
            g.moveTo( px + pr, py );
            g.arc( px, py, pr, 0, Math.PI * 2 );
          }
          g.fill();
          return;
        }
        // a bush's light is quieter, and it has no glints (owner)
        const lit = this.mix( tone, [ 255, 250, 210 ], soft ? 0.1 : 0.25 ), shade = this.mix( tone, [ 20, 50, 35 ], 0.28 );
        // simpler crowns (owner)
        for ( let j = 0; j < puffs + 1; j++ ) {
          const a2 = tr() * Math.PI * 2, d = Math.sqrt( tr() ) * R2 * 0.6;
          // a bush spreads low and wide rather than balling up (owner)
          const px = cx + Math.cos( a2 ) * d * ( soft ? 2 : 1.1 ), py = cy + Math.sin( a2 ) * d * ( soft ? 0.45 : 0.85 ) + ( soft ? R2 * 0.25 : 0 );
          const pr = R2 * ( 0.32 + tr() * 0.22 );
          const toward = Math.max( 0, Math.min( 1, 0.5 + ( px - cx ) / ( R2 * 2 ) - ( py - cy ) / ( R2 * 2 ) ) );
          const cg = g.createRadialGradient( px + pr * 0.3, py - pr * 0.35, 0, px, py, pr );
          cg.addColorStop( 0, this.rgba( this.mix( tone, lit, toward ), 1 ) );
          cg.addColorStop( 1, this.rgba( this.mix( shade, tone, toward * 0.5 ), 1 ) );
          g.fillStyle = cg;
          g.beginPath();
          g.ellipse( px, py, pr * ( soft ? 1.35 : 1 ), pr * ( soft ? 0.7 : 1 ), 0, 0, Math.PI * 2 );
          g.fill();
        }
        // a few glints of sunlit leaves on its right
        g.fillStyle = this.rgba( this.mix( tone, [ 255, 252, 220 ], 0.45 ), 0.6 );
        for ( let j = 0; j < ( soft ? 0 : 2 ); j++ ) {
          const a2 = -Math.PI * 0.45 + ( tr() - 0.5 ) * 1.2;
          const d = R2 * ( 0.45 + tr() * 0.4 );
          g.beginPath();
          g.arc( cx + Math.cos( a2 ) * d, cy + Math.sin( a2 ) * d * 0.8, R2 * ( 0.06 + tr() * 0.05 ), 0, Math.PI * 2 );
          g.fill();
        }
      };
      // tidy: fewer, compact copses, kept to a band across each slope (owner: the busy version read as a muddle)
      // none on the farthest hill (owner)
      // three times as many again (owner)
      const groups = far === 0 ? 0 : Math.round( 132 * Math.max( W, UW ) / UW );
      const plant = () => {
      // the bushes go down after every tree of the hill, so no tree covers them (owner)
      const shrubsLater: ( () => void )[] = [];
      for ( let gi = 0; gi < groups; gi++ ) {
        // one unbroken bank right across the hill, under the words too (owner)
        const gx = W / 2 + ( ( gi + tr() * 0.8 ) / groups - 0.5 ) * Math.max( W, UW ) * 1.15;
        const gTop = ridgeAt( gx );
        // the front hill's trees on one line; the middle hill's scattered up and down its slope (owner)
        const gy = detailed ? footLine( gx ) : gTop + ( base + H * 0.03 - gTop ) * ( 0.3 + tr() * 0.35 );
        // a lighter, fresher green (owner)
        const tone = this.mix( this.mix( green, pale, 0.4 + tr() * 0.15 ), white, fade * 0.8 );
        // copses of trees, with bushes only on the nearest hill (owner)
        const bushes = false;
        // each copse a dense stand of trees, a bank of them like a windbreak (owner)
        const members = 10 + Math.floor( tr() * 7 );
        const parts: { x: number; y: number; h: number; r: number }[] = [];
        for ( let i = 0; i < members; i++ ) {
          // closer together on the nearest hill (owner)
          const x = gx + ( tr() - 0.5 ) * ( detailed ? 56 : 70 ) * k * scaleT;
          const y = detailed ? footLine( x ) : gy + ( tr() - 0.5 ) * 3 * k * scaleT;
          parts.push( { x, y, h: ( 10 + tr() * 16 ) * k * scaleT, r: ( 4 + tr() * 6 ) * k * scaleT } );
        }
        // every trunk of the copse first, then every crown or bush, so none is crossed by a
        // neighbour's trunk (owner)
        const kinds = parts.map( () => Math.floor( tr() * 3 ) );
        if ( !bushes ) {
          parts.forEach( ( m, i ) => {
            const tall = kinds[ i ] === 1 ? 1.25 : 1;
            if ( detailed ) {
              // its shadow on the grass, cast to the left, away from the sun
              const sh = g.createRadialGradient( m.x - m.h * 0.15, m.y, 0, m.x - m.h * 0.15, m.y, m.h * 0.5 );
              sh.addColorStop( 0, this.rgba( this.mix( tone, [ 30, 70, 45 ], 0.4 ), 0.45 ) );
              sh.addColorStop( 1, this.rgba( tone, 0 ) );
              g.fillStyle = sh;
              g.fillRect( m.x - m.h * 0.7, m.y - m.h * 0.12, m.h * 1.1, m.h * 0.24 );
            }
            // the trunk, flared at the foot, forking into two limbs under the crown
            const tw = Math.max( 0.6, m.h * 0.065 );
            const top = m.y - m.h * 0.55 * tall;
            const tg = g.createLinearGradient( m.x - tw, 0, m.x + tw, 0 );
            tg.addColorStop( 0, this.rgba( detailed ? this.mix( trunkTone, [ 60, 40, 30 ], 0.25 ) : tone, 1 ) );
            tg.addColorStop( 1, this.rgba( detailed ? this.mix( trunkTone, [ 255, 240, 210 ], 0.2 ) : tone, 1 ) );
            g.fillStyle = tg;
            g.beginPath();
            g.moveTo( m.x - tw * 1.6, m.y );
            g.quadraticCurveTo( m.x - tw * 0.6, m.y - m.h * 0.1, m.x - tw * 0.5, top );
            g.lineTo( m.x + tw * 0.5, top );
            g.quadraticCurveTo( m.x + tw * 0.6, m.y - m.h * 0.1, m.x + tw * 1.6, m.y );
            g.closePath();
            g.fill();
            g.strokeStyle = this.rgba( detailed ? trunkTone : tone, 1 );
            g.lineWidth = tw * 0.7;
            g.lineCap = 'round';
            g.beginPath();
            g.moveTo( m.x, top + m.h * 0.05 );
            g.lineTo( m.x - m.h * 0.12, top - m.h * 0.12 );
            g.moveTo( m.x, top + m.h * 0.03 );
            g.lineTo( m.x + m.h * 0.1, top - m.h * 0.15 );
            g.stroke();
          } );
        }
        parts.forEach( ( m, i ) => {
          if ( bushes ) {
            // a thicket: low rounded bushes, no trunks, sitting on the ground
            crown( m.x, m.y - m.r * 0.55, m.r, this.mix( tone, [ 60, 110, 60 ], 0.1 ), 4 );
            return;
          }
          // three kinds of tree: a round broad crown, a tall oval one, a narrower cone
          const kind = kinds[ i ];
          const R2 = m.h * 0.42;
          if ( kind === 0 ) {
            crown( m.x, m.y - m.h * 0.74, R2 * 1.05, tone, 7 );
          } else if ( kind === 1 ) {
            crown( m.x, m.y - m.h * 0.95, R2 * 0.7, tone, 4 );
            crown( m.x, m.y - m.h * 0.7, R2 * 0.75, tone, 4 );
          } else {
            crown( m.x, m.y - m.h * 0.6, R2 * 0.85, tone, 4 );
            crown( m.x, m.y - m.h * 0.85, R2 * 0.65, tone, 3 );
            crown( m.x, m.y - m.h * 1.05, R2 * 0.42, tone, 2 );
          }
        } );
        // on the nearest hill, bushes huddled at the trees' feet, the whole copse close together (owner)
        if ( detailed ) {
          // enough of them to hide most of the trunks (owner)
          const shrubs = parts.length + Math.floor( tr() * 3 );
          for ( let i = 0; i < shrubs; i++ ) {
            // each at the foot of one of the copse's trees, never off on its own (owner)
            const at = parts[ Math.floor( tr() * parts.length ) ];
            const x = at.x + ( tr() - 0.5 ) * 8 * k * scaleT;
            // a tick in front of the trees (owner)
            const y = at.y + ( 3.5 + tr() * 2.5 ) * k * scaleT + 5;
            // a little smaller, a soft light green, paler than the trees so it reads (owner)
            const r = ( 3.2 + tr() * 3 ) * k * scaleT;
            shrubsLater.push( () => crown( x, y - r * 1.05, r, this.mix( [ 172, 208, 146 ], tone, 0.12 ), 2, true ) );
          }
        }
      }
      shrubsLater.forEach( draw => draw() );
      };
      // the front hill's trees wait until the meadow is down, so its edge never cuts their trunks (owner)
      if ( detailed ) { deferred.push( plant ); } else { plant(); }
    }
    // the meadow: fresh green, deeper toward us
    const meadow = g.createLinearGradient( 0, horizon, 0, H );
    meadow.addColorStop( 0, this.rgba( this.mix( green, pale, 0.5 ), 1 ) );
    meadow.addColorStop( 1, this.rgba( this.mix( green, [ 40, 80, 50 ], 0.15 ), 1 ) );
    g.fillStyle = meadow;
    g.fillRect( 0, horizon, W, H - horizon );
    deferred.forEach( plant => plant() );
    // the grass: fine tapered blades in a few greens, thicker and taller toward us
    const gr = this.seededLocal( 9 );
    const blades: RGB[] = [ this.mix( green, pale, 0.35 ), this.mix( green, [ 30, 70, 40 ], 0.25 ), green ];
    for ( let i = 0; i < 1600; i++ ) {
      const x = gr() * W;
      const near = Math.pow( gr(), 1.3 );
      const y = horizon + ( H - horizon ) * near;
      const h = ( 3 + 14 * near ) * k;
      const w = ( 0.5 + 1.4 * near ) * k;
      const bend = ( gr() - 0.5 ) * h * 0.6;
      g.fillStyle = this.rgba( blades[ Math.floor( gr() * blades.length ) ], 0.4 + near * 0.35 );
      g.beginPath();
      g.moveTo( x - w, y );
      g.quadraticCurveTo( x - w * 0.3 + bend * 0.4, y - h * 0.6, x + bend, y - h );
      g.quadraticCurveTo( x + w * 0.3 + bend * 0.4, y - h * 0.6, x + w, y );
      g.closePath();
      g.fill();
    }
    // a clearing of light behind the hero's words
    for ( const b of this.blocks ) {
      g.save();
      g.translate( b.x + b.w / 2, b.y + b.h / 2 );
      g.scale( b.w / 2 + 70 * k, b.h / 2 + 40 * k );
      const cl = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      cl.addColorStop( 0, this.rgba( white, 0.4 ) );
      cl.addColorStop( 1, this.rgba( white, 0 ) );
      g.fillStyle = cl;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    this.back = c;
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Spring's own: pale green, green, rose, lavender, teal. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 200, 234, 185 ], [ 85, 149, 105 ], [ 186, 97, 115 ], [ 213, 179, 244 ], [ 94, 154, 174 ] ];
  }
}
